import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { GoogleChromeLogo } from "@phosphor-icons/react";
import { LoginType, ProfileData } from "../common/types";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LoginExtension from "../services/loginExtension";
import { toast } from "react-toastify";
import { getAccountFromToken } from "../common/helpers/http";
import * as Sentry from "@sentry/react";

const CHROME_STORE_BASE =
  "https://chromewebstore.google.com/detail";

function buildExtensionUrl(extensionId: string): string {
  const id = extensionId || import.meta.env.VITE_CHROME_EXTENSION_ID;
  return `${CHROME_STORE_BASE}/${id}`;
}

interface ExtensionLoginProps {
  disabled: boolean;
  countryCode: string;
  extensionId: string;
  setAccount: Dispatch<SetStateAction<ProfileData>>;
  setLoginType: Dispatch<SetStateAction<LoginType>>;
}

function ExtensionLogin({
  disabled,
  countryCode,
  extensionId,
  setAccount,
  setLoginType,
}: ExtensionLoginProps) {
  const [isExtensionAvailable, setIsExtensionAvailable] = useState(false);
  const [isGettingAccount, setIsGettingAccount] = useState(false);
  const [tokenAccId, setTokenAccId] = useState("");
  useEffect(() => {
    const checkExtension = () => {
      if (
        typeof window.chrome === "undefined" ||
        !window.chrome?.runtime?.sendMessage
      ) {
        setIsExtensionAvailable(false);
        return;
      }
      window.chrome.runtime.sendMessage(
        extensionId || import.meta.env.VITE_CHROME_EXTENSION_ID,
        { command: "PING" },
        (response) => {
          const isAvailable = !window.chrome.runtime.lastError && response;
          setIsExtensionAvailable(isAvailable);
        }
      );
    };

    checkExtension();

    getAccountFromToken().then((acc) => setTokenAccId(acc));
    const handleInstall = (info: chrome.management.ExtensionInfo) => {
      if (info.id === (extensionId || import.meta.env.VITE_CHROME_EXTENSION_ID)) {
        checkExtension();
      }
    };

    const handleUninstall = (id: string) => {
      if (id === (extensionId || import.meta.env.VITE_CHROME_EXTENSION_ID)) {
        checkExtension();
      }
    };
    window.chrome?.management?.onInstalled?.addListener(handleInstall);
    window.chrome?.management?.onUninstalled?.addListener(handleUninstall);
    window.addEventListener("focus", checkExtension);

    return () => {
      window.chrome?.management?.onInstalled?.removeListener(handleInstall);
      window.chrome?.management?.onUninstalled?.removeListener(handleUninstall);
      window.removeEventListener("focus", checkExtension);
    };
  }, [setAccount, setLoginType, extensionId]);

  const { getAccountFromCookies } = LoginExtension();

  function getCookiesAccount(): void {
    if (!extensionId && !import.meta.env.VITE_CHROME_EXTENSION_ID) {
      toast.error("Chrome extension ID is not defined");
      return;
    }

    window.chrome.runtime.sendMessage(
      extensionId || import.meta.env.VITE_CHROME_EXTENSION_ID,
      { command: "getCookies" },
      ({ response }) => {
        setIsGettingAccount(true);
        getAccountFromCookies(response, countryCode)
          .then((profile) => {
            if (tokenAccId && profile && tokenAccId != profile.profile.id) {
              toast.error(
                "Mismatched accounts, please use the required account to reconnect"
              );
              return;
            }
            setAccount(profile.profile);
            setLoginType(LoginType.EXTENSION);
          })
          .catch((error) => {
            Sentry.setExtra("error", error);
            Sentry.captureMessage("getAccountFromCookies - Error");
            toast.error(
              "There was an error trying to log in, please try again"
            );
          })
          .finally(() => {
            setIsGettingAccount(false);
          });
      }
    );
  }

  let mainComponent;
  if (!isExtensionAvailable) {
    mainComponent = (
      <>
        {disabled ? (
          <Button
            startIcon={<GoogleChromeLogo />}
            variant="contained"
            className="wlb-button secondary"
            disabled={disabled}
          >
            Install Chrome Extension
          </Button>
        ) : (
          <a
            target="_blank"
            href={buildExtensionUrl(extensionId)}
            rel="noreferrer"
          >
            <Button
              startIcon={<GoogleChromeLogo />}
              variant="contained"
              className="wlb-button secondary"
              disabled={disabled}
            >
              Install Chrome Extension
            </Button>
          </a>
        )}
      </>
    );
  } else {
    mainComponent = (
      <LoadingButton
        onClick={getCookiesAccount}
        variant="contained"
        className="wlb-button secondary"
        loading={isGettingAccount}
        disabled={disabled}
      >
        Next
      </LoadingButton>
    );
  }

  return <>{mainComponent}</>;
}

export default ExtensionLogin;
